import { useEffect } from "react";
import { Link } from "react-router-dom";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CookieBanner from "@/components/site/CookieBanner";
import AiHouseSection from "@/components/site/AiHouseSection";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Sparkles, Home, Wrench, Leaf, Store, ShieldCheck, FileText } from "lucide-react";
import { track } from "@/lib/analytics";
import { useSeo, ORG_SCHEMA, buildFaqSchema, buildBreadcrumbSchema, buildServiceSchema } from "@/lib/seo";

const copy = {
  nl: {
    eyebrow: "Voor kopers, verkopers en eigenaren",
    title: "Alles rond jouw woning, op één plek bij jouw makelaar.",
    lead: "Verhuizen, onderhouden, verbouwen of verduurzamen: via de Tribeo marketplace van jouw makelaar regel je het bij leveranciers die zij zelf hebben geselecteerd. Plus een AI Wooncoach die jouw specifieke woning kent.",
    viaBroker: "Je gebruikt Tribeo via je makelaar, onder hun eigen merk. Wij bouwen het platform eronder.",
    cta1: "Vraag je makelaar naar Tribeo",
    cta2: "Vind een aangesloten makelaar",
    whatTitle: "Wat je krijgt via jouw makelaar",
    whatItems: [
      { icon: Store, t: "Eén marketplace voor alles rond je huis", d: "Fotografie, styling, hovenier, schilder, isolatie, zonnepanelen. Allemaal bij leveranciers die jouw makelaar persoonlijk heeft geselecteerd." },
      { icon: Sparkles, t: "AI Wooncoach die jouw huis kent", d: "Bouwjaar, energielabel, oppervlakte en installaties zijn bekend. Antwoorden die kloppen bij jouw specifieke woning." },
      { icon: ShieldCheck, t: "Veilig en duidelijk afrekenen", d: "Eén betaling, heldere factuur en levering volgens afspraak. Geen onbekende partijen, geen gedoe achteraf." },
      { icon: FileText, t: "Jouw woningdossier blijft bij jou", d: "Documenten, foto's en plattegronden op één plek. Verkoop je later? Dan kun je het dossier overdragen aan de nieuwe eigenaar." },
    ],
    sectionsTitle: "Wat de AI Wooncoach voor jou doet",
    sections: [
      { icon: Home, title: "Antwoord op je woningvragen", body: "Vraag alles over je huis, van onderhoudsschema tot verduurzaming. De Wooncoach gebruikt de woninggegevens die de makelaar heeft aangeleverd." },
      { icon: Wrench, title: "Slim onderhoudsplan", body: "Een tijdlijn op basis van bouwjaar, leeftijd van installaties en de staat van de woning. Zo weet je vooraf wanneer je cv-ketel toe is aan vervanging." },
      { icon: Leaf, title: "Verduurzaming met subsidies", body: "Routekaart voor isolatie, warmtepomp en zonnepanelen. Inclusief actuele subsidies en terugverdientijd voor jouw situatie." },
      { icon: Sparkles, title: "Direct door naar de leverancier", body: "Aan het einde van een advies kies je een leverancier die jouw makelaar heeft geselecteerd. Eén klik en je bestelling staat." },
    ],
    howTitle: "Zo werkt het",
    how: [
      { num: "01", title: "Je makelaar nodigt je uit", desc: "Je krijgt toegang tot de marketplace van jouw makelaar, onder hun eigen merk." },
      { num: "02", title: "Stel je vraag of bekijk de aanbod", desc: "Onderhoud, verbouwen, verduurzamen, kosten of tijdlijn. In gewone taal, met advies dat klopt bij jouw woning." },
      { num: "03", title: "Bestel bij een geselecteerde leverancier", desc: "Eén betaling, duidelijke afspraken, en de factuur staat in je dossier." },
    ],
    bottomTitle: "Tribeo loopt via je makelaar.",
    bottomDesc: "Vraag jouw makelaar of zij een Tribeo marketplace hebben, of stuur ons een bericht en we wijzen je op een aangesloten kantoor.",
    faqTitle: "Veelgestelde vragen",
    faq: [
      { q: "Wat kost het mij als koper, verkoper of eigenaar?", a: "Niets. Tribeo is gratis voor jou. Je betaalt alleen voor de producten en diensten die je daadwerkelijk afneemt, tegen de prijs die de leverancier vermeldt." },
      { q: "Heb ik een eigen Tribeo account nodig?", a: "Nee. Je gebruikt Tribeo via je makelaar. Zij sturen je een uitnodiging voor hun marketplace, onder hun eigen merk. Wij bouwen het platform eronder en blijven op de achtergrond." },
      { q: "Wie heeft toegang tot mijn woninggegevens?", a: "Jij en de makelaar die de marketplace beheert. Leveranciers krijgen alleen de informatie die ze nodig hebben voor hun offerte, en pas na jouw akkoord." },
      { q: "Welk AI-model draait er onder de motorkap?", a: "De Wooncoach draait op Claude van Anthropic, gehost binnen de EU. Antwoorden zijn adviserend en helpen je een goede keuze te maken." },
      { q: "Kan ik mijn woningdossier overdragen als ik verkoop?", a: "Ja. Bij verkoop kun je het woningdossier (foto's, plattegronden, documenten en onderhoudshistorie) overdragen aan de nieuwe eigenaar. Zo krijgt die direct een vliegende start." },
    ],
  },
  en: {
    eyebrow: "For buyers, sellers and owners",
    title: "Everything around your home, in one place with your agent.",
    lead: "Moving, maintaining, renovating or making your home greener: through your agent's Tribeo marketplace you arrange it with vendors they've personally selected. Plus an AI Home Advisor that knows your specific home.",
    viaBroker: "You use Tribeo through your agent, under their own brand. We build the platform underneath.",
    cta1: "Ask your agent about Tribeo",
    cta2: "Find a connected agent",
    whatTitle: "What you get through your agent",
    whatItems: [
      { icon: Store, t: "One marketplace for everything around your home", d: "Photography, staging, gardener, painter, insulation, solar. All from vendors your agent has personally selected." },
      { icon: Sparkles, t: "An AI Home Advisor that knows your home", d: "Year of construction, energy label, surface area and installations are known. Answers that fit your specific home." },
      { icon: ShieldCheck, t: "Clear and secure checkout", d: "One payment, a clear invoice and delivery as agreed. No unknown parties, no hassle afterwards." },
      { icon: FileText, t: "Your property file stays with you", d: "Documents, photos and floor plans in one place. Selling later? Hand the file over to the next owner." },
    ],
    sectionsTitle: "What the AI Home Advisor does for you",
    sections: [
      { icon: Home, title: "Answers about your home", body: "Ask anything about your house, from maintenance to sustainability. The advisor uses the property data the agent has provided." },
      { icon: Wrench, title: "Smart maintenance plan", body: "A timeline based on year of construction, age of installations and the condition of the home. Know upfront when your boiler will need replacing." },
      { icon: Leaf, title: "Sustainability with subsidies", body: "A roadmap for insulation, heat pump and solar panels. Includes current subsidies and payback time for your situation." },
      { icon: Sparkles, title: "Straight to the vendor", body: "At the end of any advice, pick a vendor your agent has selected. One click and your order is placed." },
    ],
    howTitle: "How it works",
    how: [
      { num: "01", title: "Your agent invites you", desc: "You get access to your agent's marketplace, under their own brand." },
      { num: "02", title: "Ask or browse the offering", desc: "Maintenance, renovation, sustainability, costs or timing. In plain language, with advice that fits your home." },
      { num: "03", title: "Order from a selected vendor", desc: "One payment, clear agreements, and the invoice ends up in your file." },
    ],
    bottomTitle: "Tribeo runs through your agent.",
    bottomDesc: "Ask your agent if they run a Tribeo marketplace, or send us a message and we'll point you to a connected office.",
    faqTitle: "Frequently asked questions",
    faq: [
      { q: "What does it cost me as a buyer, seller or owner?", a: "Nothing. Tribeo is free for you. You only pay for the products and services you actually order, at the price the vendor lists." },
      { q: "Do I need my own Tribeo account?", a: "No. You use Tribeo through your agent. They send you an invitation to their marketplace, under their own brand. We build the platform underneath and stay in the background." },
      { q: "Who has access to my property data?", a: "You and the agent who runs the marketplace. Vendors only receive what they need for their quote, and only after your approval." },
      { q: "Which AI model powers the advisor?", a: "The Home Advisor runs on Claude by Anthropic, hosted inside the EU. Answers are advisory and help you make a good decision." },
      { q: "Can I transfer my property file when I sell?", a: "Yes. When selling, you can transfer the property file (photos, floor plans, documents and maintenance history) to the new owner so they get a flying start." },
    ],
  },
};

const Content = () => {
  const { lang } = useLang();
  const c = copy[lang];

  useSeo(lang, {
    title: lang === "nl"
      ? "Voor kopers, verkopers en eigenaren | Tribeo"
      : "For buyers, sellers and owners | Tribeo",
    description: lang === "nl"
      ? "Vind via je makelaar geselecteerde leveranciers voor fotografie, onderhoud, verbouwen en verduurzaming. Plus een AI Wooncoach die jouw huis kent en duidelijk afrekenen."
      : "Through your agent: selected vendors for photography, maintenance, renovation and sustainability. Plus an AI Home Advisor that knows your home, and clear checkout.",
    ogTitle: lang === "nl"
      ? "Alles rond jouw woning op één plek, bij jouw makelaar"
      : "Everything around your home in one place, with your agent",
    ogDescription: lang === "nl"
      ? "Geselecteerde leveranciers, AI Wooncoach en duidelijk afrekenen. Inbegrepen bij elke Tribeo marketplace."
      : "Selected vendors, AI Home Advisor and clear checkout. Included in every Tribeo marketplace.",
    keywords: lang === "nl"
      ? "huis kopen begeleiding, huis verkopen voorbereiden, woningonderhoud regelen, verduurzaming woning, AI wooncoach, woningdossier overdragen, leveranciers via makelaar"
      : "buying a home guidance, selling a home preparation, home maintenance, home sustainability advice, AI home advisor, property file transfer, vendors through agent",
    nlPath: "/wooncoach",
    enPath: "/en/home-advisor",
    jsonLd: [
      ORG_SCHEMA,
      buildFaqSchema(c.faq),
      buildServiceSchema({
        name: lang === "nl" ? "AI Wooncoach" : "AI Home Advisor",
        description: lang === "nl"
          ? "Persoonlijk AI-woningadvies op basis van jouw woningcontext. Onderhoud, verbouwen, verduurzamen en subsidies."
          : "Personalised AI home advice based on your specific property context. Maintenance, renovation, sustainability and subsidies.",
        url: `https://tribeo.co${lang === "nl" ? "/wooncoach" : "/en/home-advisor"}`,
        audience: lang === "nl" ? "Huiseigenaren en huizenkopers" : "Homeowners and homebuyers",
      }),
      buildBreadcrumbSchema([
        { name: lang === "nl" ? "Home" : "Home", path: lang === "nl" ? "/" : "/en" },
        { name: lang === "nl" ? "AI Wooncoach" : "AI Home Advisor", path: lang === "nl" ? "/wooncoach" : "/en/home-advisor" },
      ]),
    ],
  });

  return (
    <>
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 bg-cream">
        <div className="container max-w-3xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-glow">{c.eyebrow}</span>
          <h1 className="mt-4 text-4xl md:text-6xl font-extrabold leading-[1.05]">{c.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-muted-foreground">{c.lead}</p>
          <p className="mt-4 text-sm md:text-base text-muted-foreground italic">{c.viaBroker}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lang === "en" ? "/en/contact" : "/contact"} onClick={() => track("nav_click", "wooncoach_hero_contact")}>
              <Button size="lg" className="rounded-full h-12 px-7 text-base font-bold w-full sm:w-auto">
                {c.cta2} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-[1.05] max-w-2xl">{c.whatTitle}</h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {c.whatItems.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.t} className="rounded-3xl bg-cream p-7">
                  <div className="h-11 w-11 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-extrabold leading-tight">{s.t}</h3>
                  <p className="mt-2 text-base text-foreground/80 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-soft">
        <div className="container">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-[1.05] max-w-2xl">{c.sectionsTitle}</h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-5">
            {c.sections.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="rounded-3xl bg-secondary p-7">
                  <div className="h-11 w-11 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-extrabold leading-tight">{s.title}</h3>
                  <p className="mt-2 text-base text-foreground/80 leading-relaxed">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reuse the rich AI section already on homepage */}
      <AiHouseSection />

      <section className="py-20 md:py-28 bg-soft">
        <div className="container">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-[1.05] max-w-2xl">{c.howTitle}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {c.how.map((st) => (
              <div key={st.num} className="rounded-3xl bg-background p-8 shadow-card">
                <div className="text-sm font-bold text-primary-glow">{st.num}</div>
                <div className="mt-3 text-2xl font-extrabold leading-tight">{st.title}</div>
                <p className="mt-3 text-base text-muted-foreground">{st.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background border-t border-border">
        <div className="container max-w-3xl">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight text-center">{c.faqTitle}</h2>
          <Accordion type="single" collapsible className="mt-10">
            {c.faq.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-border">
                <AccordionTrigger className="text-left text-base font-bold hover:no-underline">{item.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background">
        <div className="container max-w-3xl text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold leading-tight">{c.bottomTitle}</h2>
          <p className="mt-6 text-lg text-muted-foreground">{c.bottomDesc}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={lang === "en" ? "/en/contact" : "/contact"} onClick={() => track("nav_click", "wooncoach_bottom_contact")}>
              <Button size="lg" className="rounded-full h-12 px-7 text-base font-bold w-full sm:w-auto">
                {c.cta2} <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

const Wooncoach = () => (
  <LanguageProvider>
    <div className="min-h-screen bg-background">
      <Header />
      <main><Content /></main>
      <Footer />
      <CookieBanner />
    </div>
  </LanguageProvider>
);

export default Wooncoach;
